import { type CreateSlice } from 'zustand-slice';

import { DeliveryCheckPayload, DeliveryType, StreetAddress } from '@lib/entities';

export interface DeliveryProperties {
    deliveryType: DeliveryType;
    /**
     * This is a selected, valid delivery address.
     */
    deliveryAddress?: StreetAddress;
    deliveryCheck?: DeliveryCheckPayload;
    deliveryCheckError?: string;
    isCheckingAddress: boolean;
    isDelivery: boolean;
    isPickup: boolean;
}

export type PersistedDeliveryState = DeliveryProperties;

export interface DeliveryState extends DeliveryProperties {
    changeDeliveryType: (deliveryType: DeliveryType) => void;
    setDeliveryAddress: (address?: StreetAddress) => void;
    setDeliveryCheck: (check?: DeliveryCheckPayload) => void;
    setDeliveryCheckError: (error: string | undefined) => void;
    checkingDeliveryAddress: VoidFunction;
    checkedDeliveryAddress: (deliveryCheck: DeliveryCheckPayload | undefined) => void;
    resetAddress: VoidFunction;
}

const DefaultState: DeliveryProperties = {
    deliveryType: DeliveryType.Pickup,
    isCheckingAddress: false,
    isPickup: true,
    isDelivery: false
};

export const initDeliverySlice: CreateSlice<DeliveryState> = (set, _get) => ({
    ...DefaultState,
    setDeliveryAddress: (deliveryAddress) => set({ deliveryAddress }, 'setDeliveryAddress'),
    setDeliveryCheck: (deliveryCheck) => set({ deliveryCheck }, 'setDeliveryCheck'),
    setDeliveryCheckError: (deliveryCheckError) =>
        set({ deliveryCheckError }, 'setDeliveryCheckError'),

    changeDeliveryType: (deliveryType) => {
        set(
            {
                deliveryType,
                deliveryCheck: undefined,
                deliveryAddress: undefined,
                isPickup: deliveryType === 'Pickup',
                isDelivery: deliveryType === 'Delivery'
            },
            'changeDeliveryType'
        );
    },

    resetAddress: () =>
        set(
            { deliveryCheck: undefined, deliveryAddress: undefined, isCheckingAddress: false },
            'resetAddress'
        ),

    checkingDeliveryAddress: () => {
        set(
            {
                deliveryAddress: undefined,
                isCheckingAddress: true,
                deliveryCheck: undefined,
                deliveryCheckError: undefined
            },
            'checkingDeliveryAddress'
        );
    },

    checkedDeliveryAddress: (deliveryCheck) => {
        set(
            {
                deliveryCheck,
                isCheckingAddress: false
            },
            'checkedDeliveryAddress'
        );
    }
});

export const selectIsDelivery = (state: DeliveryState) => state.isDelivery;
